/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Popover, FormControl, Select, MenuItem } from "@mui/material";
import { makeStyles } from "@mui/styles";
import LabelledField from "components/labelled-field";
import { LINK_FORMAT_OPTIONS, LINK_STYLE_OPTIONS } from "cms/links/link/helpers";
import { isArrayNotEmpty, isStringNotEmpty } from "utils/empty-exists";

const useStyles = makeStyles(() => ({
    settingsContainer: {
        "& > .MuiPaper-root": {
            marginTop: 12,
            padding: 12,
            width: 280,
            backgroundColor: "#fff",
        },
        "& h5": {
            margin: 0,
            fontSize: "0.875rem",
            lineHeight: 1.5,
            color: "#666",
            fontWeight: 500,
        },
    },
}));

const SettingsDialog = (props) => {
    
    const {
        settings,
        onChange,
        open,
        anchorEl,
        setAnchorEl
    } = props;
    
    const {
        style: _style,
        format: _format,
    } = settings || {};
    
    const [style, setStyle] = useState(_style);
    const [format, setFormat] = useState(_format);
    
    const { t } = useTranslation();
    const classes = useStyles();
    const selectionOptions = LINK_STYLE_OPTIONS;
    const displayOptions = LINK_FORMAT_OPTIONS;
    
    const onChangeStyle = (event) => {
        const value = event.target.value;
        let updatedValue = isStringNotEmpty(value) ? value : "";
        setStyle(updatedValue);
        const nextSettings = { ...settings, style: updatedValue };
        onChange(nextSettings);
    };
    
    const onChangeFormat = (event) => {
        const value = event.target.value;
        let updatedValue =  isStringNotEmpty(value) ? value : "";
        setFormat(updatedValue);
        const nextSettings = { ...settings, format: updatedValue };
        onChange(nextSettings);
    };
    
    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            placement='top-end'
            className={classes.settingsContainer}
        >
            <h5>{t("commons.linkSettings")}</h5>
            <LabelledField label={t("commons.displayStyle")} className='labelField'>
                {isArrayNotEmpty(selectionOptions) &&
                    <FormControl sx={{ mt: 1, mb: 1, width: "100%" }} size="small">
                        <Select
                            labelId='document-select'
                            id='document-style-select'
                            value={style}
                            fullWidth={true}
                            variant='outlined'
                            onChange={(event) => onChangeStyle(event)}
                        >
                            {style === "" &&
                                <MenuItem value=''>Select...</MenuItem>
                            }
                            <MenuItem value=''>Select...</MenuItem>
                            {selectionOptions.map((option, index) =>
                                (
                                    <MenuItem
                                        key={index}
                                        value={option?.value}
                                    >
                                        {option?.name}
                                    </MenuItem>
                                )
                            )}
                        </Select>
                    </FormControl>
                }
            </LabelledField>
            <LabelledField label={t("commons.displayAs")} className='labelField'>
                {isArrayNotEmpty(displayOptions) &&
                    <FormControl sx={{ mt: 1, mb: 1, width: "100%" }} size="small">
                        <Select
                            labelId='display-as-select'
                            id='display-as-select'
                            value={format}
                            fullWidth={true}
                            variant='outlined'
                            onChange={(event) => onChangeFormat(event)}
                        >
                            {format === "" &&
                                <MenuItem value=''>Select...</MenuItem>
                            }
                            {displayOptions.map((option, index) =>
                                (
                                    <MenuItem
                                        key={index}
                                        value={option?.value}
                                    >
                                        {option?.name}
                                    </MenuItem>
                                )
                            )}
                        </Select>
                    </FormControl>
                }
            </LabelledField>
        </Popover>
    );
};

SettingsDialog.displayName = "SettingsDialog";
export { SettingsDialog };
